import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../hooks/useAuth';

interface FanArtLightboxProps {
  post: {
    id: string;
    title: string;
    content: string;
    image_url: string | null;
    author: {
      id: string;
      username: string;
      avatar_url: string | null;
    };
    like_count: number;
  };
  onClose: () => void;
  onLikeUpdate: () => void;
}

const FALLBACK_IMAGE = 'https://public.readdy.ai/ai/img_res/795988e04770d5cbd0ea26f6323a8673.jpg';

export default function FanArtLightbox({ post, onClose, onLikeUpdate }: FanArtLightboxProps) {
  const { user } = useAuth();
  const [isLiked, setIsLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(post.like_count);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      checkIfLiked();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, post.id]);

  const checkIfLiked = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('forum_thread_likes')
        .select('id')
        .eq('thread_id', post.id)
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;
      setIsLiked(!!data);
    } catch (error) {
      console.error('Error checking like status:', error);
    }
  };

  const handleToggleLike = async () => {
    if (!user || loading) return;

    setLoading(true);
    try {
      if (isLiked) {
        // Unlike
        const { error } = await supabase
          .from('forum_thread_likes')
          .delete()
          .eq('thread_id', post.id)
          .eq('user_id', user.id);

        if (error) throw error;
        setIsLiked(false);
        setLikeCount((prev) => prev - 1);
      } else {
        // Like
        const { error } = await supabase
          .from('forum_thread_likes')
          .insert({
            thread_id: post.id,
            user_id: user.id,
          });

        if (error) throw error;
        setIsLiked(true);
        setLikeCount((prev) => prev + 1);
      }
      onLikeUpdate();
    } catch (error) {
      console.error('Error toggling like:', error);
    } finally {
      setLoading(false);
    }
  };

  const imageUrl = post.image_url || FALLBACK_IMAGE;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/90 backdrop-blur-sm"
      onClick={onClose}
    >
      <div 
        className="bg-zinc-900 border border-red-900/30 max-w-5xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="grid md:grid-cols-2 gap-0">
          {/* Image Side */}
          <div className="bg-zinc-950 flex items-center justify-center p-8">
            <img
              src={imageUrl}
              alt={post.title}
              className="max-w-full max-h-[70vh] object-contain"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                if (target.src !== FALLBACK_IMAGE) {
                  target.src = FALLBACK_IMAGE;
                }
              }}
            />
          </div>

          {/* Info Side */}
          <div className="flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-red-900/20">
              <h2 className="text-2xl font-['Cinzel'] text-zinc-100 pr-4">
                {post.title}
              </h2>
              <button
                onClick={onClose}
                className="w-8 h-8 flex items-center justify-center text-zinc-400 hover:text-zinc-300 transition-colors cursor-pointer flex-shrink-0"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            {/* Author Info */}
            <div className="p-6 border-b border-red-900/20">
              <Link
                to={`/profile/writer/${post.author.id}`}
                className="flex items-center gap-3 hover:opacity-80 transition-opacity"
              >
                {post.author.avatar_url ? (
                  <img
                    src={post.author.avatar_url}
                    alt={post.author.username}
                    className="w-12 h-12 rounded-full object-cover border border-red-900/30"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-red-900/20 border border-red-900/30 flex items-center justify-center">
                    <i className="ri-user-line text-red-500 text-xl"></i>
                  </div>
                )}
                <div>
                  <div className="text-sm text-zinc-500 font-['Crimson_Text']">Artist</div>
                  <div className="text-base text-red-400 font-['Cinzel']">
                    {post.author.username}
                  </div>
                </div>
              </Link>
            </div>

            {/* Description */}
            <div className="flex-1 p-6 overflow-y-auto">
              <h3 className="text-sm font-['Cinzel'] text-zinc-400 mb-3">Description</h3>
              <p className="text-zinc-300 font-['Crimson_Text'] leading-relaxed whitespace-pre-wrap">
                {post.content}
              </p>
            </div>

            {/* Like Button */}
            <div className="p-6 border-t border-red-900/20">
              {user ? (
                <button
                  onClick={handleToggleLike}
                  disabled={loading}
                  className={`w-full flex items-center justify-center gap-2 px-6 py-3 border transition-all cursor-pointer whitespace-nowrap ${
                    isLiked
                      ? 'bg-red-900/20 border-red-900 text-red-400'
                      : 'bg-zinc-900/50 border-red-900/20 text-zinc-400 hover:border-red-900/40'
                  }`}
                >
                  <i className={`${isLiked ? 'ri-heart-fill' : 'ri-heart-line'} text-xl`}></i>
                  <span className="font-['Cinzel']">
                    {isLiked ? 'Liked' : 'Like'} ({likeCount})
                  </span>
                </button>
              ) : (
                <Link
                  to="/auth/login"
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-zinc-900/50 border border-red-900/20 text-zinc-400 hover:border-red-900/40 transition-all whitespace-nowrap"
                >
                  <i className="ri-heart-line text-xl"></i>
                  <span className="font-['Cinzel']">Sign in to Like ({likeCount})</span>
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}